import React, { useEffect, useRef, useState } from "react";
import { coach, quotaApi } from "../lib/api";
import { toast } from "sonner";
import { ArrowUp, BookHeart } from "lucide-react";
import PaywallModal from "../components/PaywallModal";
import SaveToJournalModal from "../components/SaveToJournalModal";

export default function CoachPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(`chat-${Date.now()}`);
  const [quota, setQuota] = useState(null);
  const [paywall, setPaywall] = useState({ open: false, reason: "" });
  const [saveJournal, setSaveJournal] = useState({ open: false, title: "", content: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    coach
      .history()
      .then((rows) => {
        if (rows && rows.length) {
          const lastSession = rows[rows.length - 1].session_id;
          setMessages(rows.filter((r) => r.session_id === lastSession));
        }
      })
      .catch(() => {});
    quotaApi.get().then(setQuota).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const my = { role: "user", content: input, id: `local-${Date.now()}` };
    setMessages((m) => [...m, my]);
    setBusy(true);
    const text = input;
    setInput("");
    try {
      const res = await coach.send(text, sessionId);
      setMessages((m) => [...m, { role: "assistant", content: res.reply, id: `ai-${Date.now()}` }]);
      quotaApi.get().then(setQuota).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall({ open: true, reason: "You've used your 1 free Coach reply for today. LifeGuide Pro is unlimited." });
      } else {
        toast.error(err?.response?.data?.detail || "Let’s try that again");
      }
      setMessages((m) => m.filter((mm) => mm.id !== my.id));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-180px)]">
      <header className="mb-4 fade-in-up">
        <h1 className="font-display text-3xl text-[#2E2E2E]">Your Coach</h1>
        <p className="text-sm text-[#6B6B6B] leading-relaxed mt-1">
          Ask anything that’s on your heart. There’s no rush.
          {quota && quota.plan === "free" && (
            <> · <span className="text-[#4F6F52]">{Math.max(0, quota.chat_limit - quota.chat_used)} of {quota.chat_limit} replies left today</span></>
          )}
        </p>
      </header>

      <div
        ref={scrollRef}
        data-testid="chat-thread"
        className="flex-1 overflow-y-auto rounded-3xl border border-[#DCE5DB] bg-[#EEF3EC]/60 p-5 space-y-3 backdrop-blur-sm"
      >
        {messages.length === 0 && (
          <div className="text-center text-[#6B6B6B] py-14 px-6">
            <p className="font-display text-xl text-[#2E2E2E] mb-2">A quiet conversation awaits.</p>
            <p className="text-sm leading-relaxed max-w-sm mx-auto">
              Try something like: <span className="italic">“I had a rough fight with my brother — how do I make it right?”</span>
            </p>
          </div>
        )}
        {messages.map((m, i) => {
          const prevUser =
            m.role === "assistant" && i > 0 && messages[i - 1].role === "user"
              ? messages[i - 1].content
              : null;
          return (
            <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} fade-in-up`}>
              <div className="max-w-[85%] flex flex-col gap-1.5 items-end">
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`rounded-3xl px-4 py-3 text-[15px] leading-[1.7] ${
                    m.role === "user" ? "bubble-user rounded-br-md" : "bubble-ai rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
                {m.role === "assistant" && (
                  <button
                    data-testid="coach-save-to-journal"
                    onClick={() => {
                      const q = (prevUser || "").trim();
                      const a = (m.content || "").trim();
                      const content = q
                        ? `Me:\n${q}\n\nCoach:\n${a}`
                        : `Coach:\n${a}`;
                      setSaveJournal({
                        open: true,
                        title: q ? q.slice(0, 80) : "A note from Coach",
                        content,
                      });
                    }}
                    className="self-start text-[12.5px] font-medium text-[#3F5A42] hover:text-[#2E4531] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#E1ECDD] hover:bg-[#D0DFCB] border border-[#B7CCB1]/70 transition"
                  >
                    <BookHeart size={12} strokeWidth={2.2} /> Save to my Journal
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {busy && (
          <div className="flex justify-start fade-in-up">
            <div className="bubble-ai rounded-3xl rounded-bl-md px-4 py-3 text-sm flex items-center gap-2">
              <span className="text-[#6B6B6B] mr-1">Thinking</span>
              <span className="dot-1 w-1.5 h-1.5 rounded-full bg-[#7FA7B0] inline-block" />
              <span className="dot-2 w-1.5 h-1.5 rounded-full bg-[#7FA7B0] inline-block" />
              <span className="dot-3 w-1.5 h-1.5 rounded-full bg-[#7FA7B0] inline-block" />
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="mt-4 flex gap-2.5">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type what’s on your heart..."
          className="flex-1 bg-white border border-[#DCE5DB] rounded-full px-5 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
        />
        <button
          data-testid="chat-send"
          type="submit"
          disabled={busy || !input.trim()}
          className="bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-50 text-white rounded-full px-5 transition flex items-center gap-2 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
        >
          <ArrowUp size={18} />
        </button>
      </form>
      <PaywallModal
        open={paywall.open}
        reason={paywall.reason}
        onClose={() => setPaywall({ open: false, reason: "" })}
      />
      <SaveToJournalModal
        open={saveJournal.open}
        initialTitle={saveJournal.title}
        initialContent={saveJournal.content}
        source="coach"
        sourceRef={sessionId}
        onClose={() => setSaveJournal({ open: false, title: "", content: "" })}
        onLockedWrite={() =>
          setPaywall({
            open: true,
            reason: "Your LifeGuide journey is saved. Upgrade to save entries to your Journal.",
          })
        }
      />
    </div>
  );
}
